import React, { useCallback } from 'react';
import { browserSupportsWebAuthn, startRegistration } from '@simplewebauthn/browser';
import { App } from 'antd';
import { PasskeyManagementCard } from '@digiwedge/access-control-pages';
import { useGlobalStore, type GlobalState } from '@digiwedge/global-state';

import { useIdp } from '../auth/IdpProvider.js';
import { isPasskeysEnabled } from '../auth/passkeyFeature.js';

const PASSKEY_STEP_UP_MAX_AGE_SECONDS = 10 * 60;
const PASSKEY_STEP_UP_MAX_AGE_MILLISECONDS = PASSKEY_STEP_UP_MAX_AGE_SECONDS * 1000;

function decodeJwtNumberClaim(token: string, claim: 'auth_time'): number | null {
  const [, payload] = token.split('.');
  if (!payload) return null;
  try {
    const padded = payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), '=');
    const decoded = JSON.parse(window.atob(padded.replace(/-/g, '+').replace(/_/g, '/'))) as Record<
      string,
      unknown
    >;
    const value = decoded[claim];
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

export default function PasskeySettingsCard(): React.ReactElement {
  const idp = useIdp();
  const { message } = App.useApp();
  const token = useGlobalStore((state: GlobalState) => state.token);

  const ensureRecentSecuritySession = useCallback((): boolean => {
    if (!token) {
      return true;
    }

    const authTime = decodeJwtNumberClaim(token, 'auth_time');
    if (authTime === null) {
      message.warning('Sign in again before managing passkeys.');
      return false;
    }

    const nowMs = Date.now();
    const authTimeMs = authTime * 1000;
    const ageMs = nowMs - authTimeMs;
    if (ageMs < 0 || ageMs > PASSKEY_STEP_UP_MAX_AGE_MILLISECONDS) {
      message.warning('Sign in again before managing passkeys.');
      return false;
    }

    return true;
  }, [message, token]);

  return (
    <PasskeyManagementCard
      api={idp}
      enabled={isPasskeysEnabled()}
      ensureRecentSecuritySession={ensureRecentSecuritySession}
      supported={browserSupportsWebAuthn()}
      startRegistration={(optionsJSON: unknown) =>
        startRegistration({
          optionsJSON: optionsJSON as Parameters<typeof startRegistration>[0]['optionsJSON'],
        })
      }
    />
  );
}
